import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { MdAddchart } from 'react-icons/md';
import { FaTimes, FaRegCalendarAlt, FaCheckCircle, FaDownload } from 'react-icons/fa';
import moment from 'moment';
import { Button, Pill } from '../../components/EntityList';
import FilterForm from '../../components/FilterForm';
import { MODULE_CLASSES } from './reportsCatalogData';

/**
 * Sticky right-column panel: empty state, or a report's header with a body
 * that swaps between idle (filters/fixed-period strip), preparing (spinner)
 * and ready (download) — the same idle->preparing->ready shape as ExportModal.
 */
function ReportConfigPanel({ report, filterFormProps, onClose, onGenerate, genState, result, onBack, onDownload, isHeavyReport, heavyReportBusy }) {
	const [showDescription, setShowDescription] = useState(false);

	if (!report) {
		return (
			<div className='tw:flex tw:flex-col tw:items-center tw:gap-3 tw:rounded-panel tw:border tw:border-border tw:bg-surface tw:px-6 tw:py-12 tw:text-center'>
				<span className='tw:grid tw:h-[52px] tw:w-[52px] tw:place-items-center tw:rounded-panel tw:bg-surface-3 tw:text-ink-2'>
					<MdAddchart size={22} />
				</span>
				<div>
					<h3 className='tw:m-0 tw:text-[15px] tw:font-semibold tw:text-ink-0'>Elegí un reporte</h3>
					<p className='tw:mx-auto tw:mt-1.5 tw:mb-0 tw:max-w-[240px] tw:text-[13px] tw:text-ink-2'>
						Seleccioná un reporte del catálogo para configurar sus filtros.
					</p>
				</div>
			</div>
		);
	}

	const { Icon } = report;
	const moduleClasses = MODULE_CLASSES[report.module];

	return (
		<div className='tw:rounded-panel tw:border tw:border-border tw:bg-surface'>
			<div className='tw:flex tw:items-start tw:gap-3 tw:border-b tw:border-border tw:px-5 tw:py-4'>
				<span className={`tw:flex tw:h-[34px] tw:w-[34px] tw:shrink-0 tw:items-center tw:justify-center tw:rounded-[9px] tw:text-white ${moduleClasses.tile}`}>
					<Icon size={16} />
				</span>
				<div className='tw:min-w-0 tw:flex-1'>
					<h3 className='tw:m-0 tw:text-[14px] tw:font-semibold tw:text-ink-0'>{report.title}</h3>
					<p className='tw:mt-0.5 tw:mb-0 tw:text-[11.5px] tw:text-ink-3'>{report.subtitle}</p>
					<button
						type='button'
						onClick={() => setShowDescription((prev) => !prev)}
						className='tw:mt-1 tw:border-0 tw:bg-transparent tw:p-0 tw:text-[11.5px] tw:font-medium tw:text-accent tw:hover:underline'
					>
						{showDescription ? 'Ocultar detalle' : '¿Qué incluye?'}
					</button>
					{showDescription && (
						<p className='tw:mt-1.5 tw:mb-0 tw:text-[11.5px] tw:leading-[1.4] tw:text-ink-3'>{report.description}</p>
					)}
				</div>
				<button
					type='button'
					onClick={onClose}
					aria-label='Cerrar'
					className='tw:grid tw:h-7 tw:w-7 tw:shrink-0 tw:place-items-center tw:rounded-[7px] tw:border-0 tw:bg-transparent tw:text-ink-2 tw:hover:bg-surface-3'
				>
					<FaTimes size={14} />
				</button>
			</div>

			{genState === 'preparing' && (
				<div className='tw:flex tw:flex-col tw:items-center tw:gap-1 tw:px-5 tw:py-12 tw:text-center'>
					<div className='tw:mb-3 tw:h-[50px] tw:w-[50px] tw:animate-spin tw:rounded-full tw:border-[3px] tw:border-surface-3 tw:border-t-accent' />
					<div className='tw:font-semibold tw:text-ink-0'>{isHeavyReport ? 'Agendando reporte…' : 'Generando reporte…'}</div>
					<div className='tw:text-[12.5px] tw:text-ink-3'>{isHeavyReport ? 'Preparando la generación en segundo plano.' : 'Compilando datos del período.'}</div>
				</div>
			)}

			{genState === 'ready' && (
				<div className='tw:flex tw:flex-col tw:gap-4 tw:px-5 tw:py-9'>
					<div className='tw:flex tw:flex-col tw:items-center tw:gap-1 tw:text-center'>
						<div className='tw:mb-2 tw:flex tw:h-[52px] tw:w-[52px] tw:items-center tw:justify-center tw:rounded-full tw:bg-ok-soft tw:text-ok tw:text-[22px]'>
							<FaCheckCircle />
						</div>
						<div className='tw:text-[15px] tw:font-semibold tw:text-ink-0'>Reporte listo</div>
						<div className='tw:font-mono tw:text-[12.5px] tw:text-ink-3'>{result?.filename}</div>
					</div>
					<div className='tw:flex tw:gap-2'>
						<Button variant='default' className='tw:flex-1 tw:justify-center' onClick={onBack}>
							Volver
						</Button>
						<Button variant='primary' icon={<FaDownload />} className='tw:flex-1 tw:justify-center' onClick={onDownload}>
							Descargar
						</Button>
					</div>
				</div>
			)}

			{genState === 'idle' && (
				report.hasFilters ? (
					<FilterForm
						{...filterFormProps}
						variant='panel'
						reportTitle={report.title}
						submitDisabled={isHeavyReport && heavyReportBusy}
						submitLabel={isHeavyReport ? 'Solicitar reporte' : 'Generar reporte'}
						submitHelp={isHeavyReport
							? (heavyReportBusy
								? 'Ya hay un reporte pesado en proceso. Cuando finalice vas a poder solicitar otro.'
								: 'Este reporte procesa mucha información. Se generará en segundo plano y aparecerá en “Reportes generados”; podés seguir usando el sistema.')
							: undefined}
					/>
				) : (
					<div className='tw:flex tw:flex-col tw:gap-[18px] tw:px-5 tw:py-[18px]'>
						<div className='tw:flex tw:items-center tw:gap-2 tw:rounded-[7px] tw:bg-surface-2 tw:px-3 tw:py-2.5'>
							<FaRegCalendarAlt className='tw:shrink-0 tw:text-ink-2' size={14} />
							<span className='tw:text-[13px] tw:text-ink-1'>
								Mes actual · {moment().format('MMMM YYYY')}
							</span>
							<span className='tw:ml-auto'><Pill tone='accent'>Fijo</Pill></span>
						</div>
						<div className='tw:rounded-[7px] tw:border tw:border-border tw:bg-surface-2 tw:px-3 tw:py-2.5 tw:text-[12px] tw:text-ink-3'>
							Se generará &quot;{report.title}&quot; en formato XLSX.
						</div>
						<Button variant='primary' icon={<MdAddchart size={15} />} className='tw:w-full tw:justify-center' onClick={onGenerate}>
							Generar reporte
						</Button>
					</div>
				)
			)}
		</div>
	);
}

ReportConfigPanel.propTypes = {
	report: PropTypes.shape({
		title: PropTypes.string,
		subtitle: PropTypes.string,
		description: PropTypes.string,
		module: PropTypes.string,
		Icon: PropTypes.elementType,
		hasFilters: PropTypes.bool,
	}),
	filterFormProps: PropTypes.object,
	onClose: PropTypes.func.isRequired,
	onGenerate: PropTypes.func.isRequired,
	genState: PropTypes.oneOf(['idle', 'preparing', 'ready']).isRequired,
	result: PropTypes.shape({ filename: PropTypes.string }),
	onBack: PropTypes.func.isRequired,
	onDownload: PropTypes.func.isRequired,
	isHeavyReport: PropTypes.bool,
	heavyReportBusy: PropTypes.bool,
};

export default ReportConfigPanel;
